import * as signalR from '@microsoft/signalr';
import { Appointment } from './appointmentService';
import { authService } from './authService';
import { appointmentService } from './appointmentService';
import { apiHealthService } from './apiHealthService';
import { offlineService } from './offlineService';

const API_URL = process.env.REACT_APP_API_URL || apiHealthService.getApiUrl();

class SignalRService {
    private hubConnection: signalR.HubConnection | null = null;
    private currentBusinessId: string | null = null;
    private currentUserId: string | null = null;
    private connectionPromise: Promise<void> | null = null;
    private appointmentCallbacks: {
        created: ((appointment: Appointment) => void)[];
        updated: ((appointment: Appointment) => void)[];
        deleted: ((appointmentId: string) => void)[];
        recurringInstanceDeleted: ((data: { recurringAppointmentId: string, instanceDate: string }) => void)[];
        recurringSeriesDeleted: ((data: { recurringAppointmentId: string, deleteOption: string }) => void)[];
    } = {
        created: [],
        updated: [],
        deleted: [],
        recurringInstanceDeleted: [],
        recurringSeriesDeleted: []
    };

    public async startConnection(userId: string): Promise<void> {
        if (this.connectionPromise && this.currentUserId === userId) {
            return this.connectionPromise;
        }
        
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            if (this.currentUserId === userId) {
                console.log('SignalR: Already connected for user:', userId);
                return Promise.resolve();
            }
            console.log('SignalR: Stopping existing connection for user change');
            await this.stopConnection();
        }

        if (this.hubConnection?.state === signalR.HubConnectionState.Connecting ||
            this.hubConnection?.state === signalR.HubConnectionState.Reconnecting) {
            console.log('SignalR: Connection already in progress, waiting...');
            return this.connectionPromise || Promise.resolve();
        }

        const token = authService.getToken();
        if (!token) {
            console.error('SignalR: No authentication token available');
            return Promise.reject(new Error('No authentication token available'));
        }

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(`${API_URL}/hubs/appointment`, {
                accessTokenFactory: () => token,
                skipNegotiation: false,
                transport: signalR.HttpTransportType.WebSockets
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: retryContext => {
                    // If we're offline, use much longer delays to reduce excessive requests
                    if (!offlineService.getOnlineStatus()) {
                        return Math.min(60000 * Math.pow(2, retryContext.previousRetryCount), 300000); // 1-5 minutes
                    }
                    
                    if (retryContext.previousRetryCount === 0) {
                        return 0;
                    }
                    if (retryContext.elapsedMilliseconds < 60000) {
                        // First few retries, try quickly
                        return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 10000);
                    }
                    // After 60 seconds, space out the retries more
                    return 30000;
                }
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();

        this.connectionPromise = new Promise<void>((resolve, reject) => {
            if (!this.hubConnection) {
                reject(new Error('Hub connection is null'));
                return;
            }

            this.hubConnection.start()
                .then(() => {
                    console.log('SignalR: Connected successfully');
                    this.currentUserId = userId;
                    return this.joinUserGroup(userId);
                })
                .then(() => {
                    this.setupEventHandlers();
                    resolve();
                })
                .catch(err => {
                    console.error('SignalR: Error while establishing connection:', err);
                    this.hubConnection = null;
                    this.currentUserId = null;
                    this.connectionPromise = null;
                    reject(err);
                });
        });

        return this.connectionPromise;
    }

    private async joinUserGroup(userId: string) {
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.hubConnection.invoke('JoinUserGroup', userId);
                console.log('SignalR: Joined user group:', userId);
            } catch (err) {
                console.error('SignalR: Error joining user group:', err);
                throw err;
            }
        } else {
            console.warn('SignalR: Cannot join user group - not connected');
        }
    }

    private setupEventHandlers() {
        if (!this.hubConnection) {
            console.warn('SignalR: Cannot setup event handlers - no connection');
            return;
        }

        this.hubConnection.onreconnecting((error) => {
            console.log('SignalR: Attempting to reconnect...', error);
        });

        this.hubConnection.onreconnected(async (connectionId) => {
            console.log('SignalR: Reconnected. ConnectionId:', connectionId);
            if (this.currentUserId) {
                try {
                    await this.joinUserGroup(this.currentUserId);
                } catch (error) {
                    console.error('SignalR: Error rejoining user group after reconnection:', error);
                }
            }
            if (this.currentBusinessId) {
                try {
                    await this.joinBusinessGroup(this.currentBusinessId);
                } catch (error) {
                    console.error('SignalR: Error rejoining business group after reconnection:', error);
                }
            }
        });

        this.hubConnection.onclose((error) => {
            console.log('SignalR: Connection closed:', error);
            
            // Store the current user ID before clearing it
            const userId = this.currentUserId;
            const businessId = this.currentBusinessId;
            
            // Clear connection state
            this.hubConnection = null;
            this.currentUserId = null;
            this.currentBusinessId = null;
            this.connectionPromise = null;
            
            // Only attempt to restart the connection if we're online
            if (userId && offlineService.getOnlineStatus()) {
                console.log(`SignalR: Will attempt to reconnect for user ${userId} in 5 seconds`);
                setTimeout(() => {
                    // Check again if we're still online before attempting reconnection
                    if (offlineService.getOnlineStatus()) {
                        console.log(`SignalR: Attempting to reconnect for user ${userId}`);
                        this.startConnection(userId)
                            .then(() => {
                                console.log(`SignalR: Successfully reconnected for user ${userId}`);
                                // Rejoin business group if needed
                                if (businessId) {
                                    this.joinBusinessGroup(businessId)
                                        .then(() => console.log(`SignalR: Rejoined business group ${businessId}`))
                                        .catch(err => console.error(`SignalR: Failed to rejoin business group ${businessId}:`, err));
                                }
                            })
                            .catch(err => {
                                console.error('SignalR: Failed to restart connection:', err);
                            });
                    } else {
                        console.log(`SignalR: Skipping reconnection attempt - offline mode detected`);
                    }
                }, 5000);
            } else if (userId) {
                console.log(`SignalR: Skipping reconnection attempt - offline mode detected`);
            }
        });

        this.hubConnection.on('AppointmentCreated', (appointment: Appointment) => {
            console.log('SignalR: Received AppointmentCreated event:', appointment);
            this.appointmentCallbacks.created.forEach(callback => {
                try {
                    callback(appointment);
                } catch (error) {
                    console.error('SignalR: Error in AppointmentCreated callback:', error);
                }
            });
        });

        this.hubConnection.on('AppointmentUpdated', (appointment: Appointment) => {
            console.log('SignalR: Received AppointmentUpdated event:', appointment);
            
            // Normalize timezone for the appointment to ensure consistent display
            const normalizedAppointment = appointmentService.normalizeAppointmentTimezone(appointment);
            
            console.log('SignalR: Normalized appointment timezone for display:', {
                before: appointment.startTime,
                after: normalizedAppointment.startTime,
                timeZone: normalizedAppointment.timeZoneId
            });
            
            this.appointmentCallbacks.updated.forEach(callback => {
                try {
                    callback(normalizedAppointment);
                } catch (error) {
                    console.error('SignalR: Error in AppointmentUpdated callback:', error);
                }
            });
        });

        this.hubConnection.on('AppointmentDeleted', (appointmentId: string) => {
            console.log('SignalR: Received AppointmentDeleted event:', appointmentId);
            this.appointmentCallbacks.deleted.forEach(callback => {
                try {
                    callback(appointmentId);
                } catch (error) {
                    console.error('SignalR: Error in AppointmentDeleted callback:', error);
                }
            });
        });

        this.hubConnection.on('RecurringInstanceDeleted', (data: { recurringAppointmentId: string, instanceDate: string }) => {
            console.log('SignalR: Received RecurringInstanceDeleted event:', data);
            this.appointmentCallbacks.recurringInstanceDeleted.forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('SignalR: Error in RecurringInstanceDeleted callback:', error);
                }
            });
        });

        this.hubConnection.on('RecurringSeriesDeleted', (data: { recurringAppointmentId: string, deleteOption: string }) => {
            console.log('SignalR: Received RecurringSeriesDeleted event:', data);
            this.appointmentCallbacks.recurringSeriesDeleted.forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('SignalR: Error in RecurringSeriesDeleted callback:', error);
                }
            });
        });
    }

    public async joinBusinessGroup(businessId: string, retryCount: number = 0): Promise<void> {
        if (!businessId) {
            console.warn('SignalR: Cannot join business group - no business ID provided');
            return;
        }
        
        if (this.currentBusinessId === businessId) {
            console.log('SignalR: Already in business group:', businessId);
            return;
        }

        if (this.currentBusinessId) {
            console.log('SignalR: Leaving current business group before joining new one');
            try {
                await this.leaveBusinessGroup(this.currentBusinessId);
            } catch (error) {
                console.warn('SignalR: Error leaving current business group:', error);
                // Continue anyway, as we want to join the new group
            }
        }

        // Wait for connection to be established if it's not already
        if (this.connectionPromise) {
            try {
                await this.connectionPromise;
            } catch (error) {
                console.error('SignalR: Failed to establish connection before joining business group:', error);
                
                // If we've already retried too many times, give up
                if (retryCount >= 3) {
                    throw error;
                }
                
                // Otherwise, try to reconnect and then retry joining the business group
                console.log(`SignalR: Attempting to reconnect before joining business group (retry ${retryCount + 1})`);
                if (this.currentUserId) {
                    try {
                        await this.startConnection(this.currentUserId);
                        return this.joinBusinessGroup(businessId, retryCount + 1);
                    } catch (reconnectError) {
                        console.error('SignalR: Failed to reconnect:', reconnectError);
                        throw error; // Throw the original error
                    }
                } else {
                    throw error;
                }
            }
        }

        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.hubConnection.invoke('JoinBusinessGroup', businessId);
                this.currentBusinessId = businessId;
                console.log('SignalR: Joined business group:', businessId);
            } catch (err) {
                console.error('SignalR: Error joining business group:', err);
                
                // If we've already retried too many times, give up
                if (retryCount >= 3) {
                    throw err;
                }
                
                // Otherwise, try to reconnect and then retry joining the business group
                console.log(`SignalR: Attempting to reconnect before joining business group (retry ${retryCount + 1})`);
                if (this.currentUserId) {
                    try {
                        await this.startConnection(this.currentUserId);
                        return this.joinBusinessGroup(businessId, retryCount + 1);
                    } catch (reconnectError) {
                        console.error('SignalR: Failed to reconnect:', reconnectError);
                        throw err; // Throw the original error
                    }
                } else {
                    throw err;
                }
            }
        } else {
            console.warn('SignalR: Cannot join business group - not connected');
            
            // If we've already retried too many times, give up
            if (retryCount >= 3) {
                throw new Error('SignalR connection not established');
            }
            
            // Otherwise, try to reconnect and then retry joining the business group
            console.log(`SignalR: Attempting to reconnect before joining business group (retry ${retryCount + 1})`);
            if (this.currentUserId) {
                try {
                    await this.startConnection(this.currentUserId);
                    return this.joinBusinessGroup(businessId, retryCount + 1);
                } catch (reconnectError) {
                    console.error('SignalR: Failed to reconnect:', reconnectError);
                    throw new Error('SignalR connection not established and no user ID available');
                }
            } else {
                throw new Error('SignalR connection not established and no user ID available');
            }
        }
    }

    public async leaveBusinessGroup(businessId: string) {
        if (!businessId || !this.hubConnection || this.hubConnection.state !== signalR.HubConnectionState.Connected) return;

        try {
            await this.hubConnection.invoke('LeaveBusinessGroup', businessId);
            if (this.currentBusinessId === businessId) {
                this.currentBusinessId = null;
            }
            console.log(`Left business group: ${businessId}`);
        } catch (err) {
            console.error('Error leaving business group:', err);
            throw err;
        }
    }

    private async leaveUserGroup(userId: string) {
        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.hubConnection.invoke('LeaveUserGroup', userId);
                console.log(`Left user group: ${userId}`);
            } catch (err) {
                console.error('Error leaving user group:', err);
                throw err;
            }
        }
    }

    public onAppointmentCreated(callback: (appointment: Appointment) => void) {
        this.appointmentCallbacks.created.push(callback);
        return () => {
            this.appointmentCallbacks.created = this.appointmentCallbacks.created.filter(cb => cb !== callback);
        };
    }

    public onAppointmentUpdated(callback: (appointment: Appointment) => void) {
        this.appointmentCallbacks.updated.push(callback);
        return () => {
            this.appointmentCallbacks.updated = this.appointmentCallbacks.updated.filter(cb => cb !== callback);
        };
    }

    public onAppointmentDeleted(callback: (appointmentId: string) => void) {
        this.appointmentCallbacks.deleted.push(callback);
        return () => {
            this.appointmentCallbacks.deleted = this.appointmentCallbacks.deleted.filter(cb => cb !== callback);
        };
    }

    public onRecurringInstanceDeleted(callback: (data: { recurringAppointmentId: string, instanceDate: string }) => void) {
        this.appointmentCallbacks.recurringInstanceDeleted.push(callback);
        return () => {
            this.appointmentCallbacks.recurringInstanceDeleted = this.appointmentCallbacks.recurringInstanceDeleted.filter(cb => cb !== callback);
        };
    }

    public onRecurringSeriesDeleted(callback: (data: { recurringAppointmentId: string, deleteOption: string }) => void) {
        this.appointmentCallbacks.recurringSeriesDeleted.push(callback);
        return () => {
            this.appointmentCallbacks.recurringSeriesDeleted = this.appointmentCallbacks.recurringSeriesDeleted.filter(cb => cb !== callback);
        };
    }

    public async stopConnection(): Promise<void> {
        this.connectionPromise = null;
        
        if (this.hubConnection) {
            try {
                if (this.currentUserId && this.hubConnection.state === signalR.HubConnectionState.Connected) {
                    await this.leaveUserGroup(this.currentUserId);
                }
                
                if (this.currentBusinessId && this.hubConnection.state === signalR.HubConnectionState.Connected) {
                    await this.leaveBusinessGroup(this.currentBusinessId);
                }
                
                await this.hubConnection.stop();
                console.log('SignalR connection stopped');
            } catch (err) {
                console.error('Error stopping SignalR connection:', err);
            } finally {
                this.hubConnection = null;
                this.currentUserId = null;
                this.currentBusinessId = null;
            }
        }
    }
}

export const signalRService = new SignalRService(); 